import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { ListChecks, AlertOctagon, Timer, ArrowUpRight } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../api/api';

const formatHours = (h) => {
  if (h == null || isNaN(h)) return '—';
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
};

const WorkflowInsightsWidget = ({ headers }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.getWorkflowInsights(headers);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load insights');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [headers]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
    >
      <Card
        data-testid="workflow-insights-widget"
        className="border border-slate-200 bg-white hover:shadow-md transition-all duration-200 cursor-pointer"
        onClick={() => navigate('/workflows')}
      >
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-1 h-5 rounded-full bg-indigo-500" />
              <p className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'var(--font-heading)' }}>
                Workflow Insights
              </p>
            </div>
            <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500" />
          </div>

          {loading ? (
            <div className="text-xs text-slate-400 py-6 text-center">Loading…</div>
          ) : error ? (
            <div className="text-xs text-amber-600 py-6 text-center" data-testid="workflow-insights-error">{error}</div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              <div
                className="text-center p-3 rounded-lg bg-indigo-50 border border-indigo-100"
                data-testid="insight-pending"
              >
                <ListChecks className="w-4 h-4 text-indigo-500 mx-auto mb-1" />
                <p className="text-2xl font-bold text-indigo-600 leading-none" style={{ fontFamily: 'var(--font-heading)' }}>
                  {data.pending_on_me}
                </p>
                <p className="text-[10px] text-indigo-500 font-medium mt-1">Pending on me</p>
              </div>

              <div
                className={`text-center p-3 rounded-lg border ${data.overdue_count > 0 ? 'bg-red-50 border-red-100' : 'bg-emerald-50 border-emerald-100'}`}
                data-testid="insight-overdue"
              >
                <AlertOctagon className={`w-4 h-4 mx-auto mb-1 ${data.overdue_count > 0 ? 'text-red-500' : 'text-emerald-500'}`} />
                <p
                  className={`text-2xl font-bold leading-none ${data.overdue_count > 0 ? 'text-red-600' : 'text-emerald-600'}`}
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  {data.overdue_count}
                </p>
                <p className={`text-[10px] font-medium mt-1 ${data.overdue_count > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                  Overdue
                </p>
              </div>

              <div
                className="text-center p-3 rounded-lg bg-slate-50 border border-slate-200"
                data-testid="insight-avg-time"
              >
                <Timer className="w-4 h-4 text-slate-500 mx-auto mb-1" />
                <p className="text-2xl font-bold text-slate-700 leading-none" style={{ fontFamily: 'var(--font-heading)' }}>
                  {formatHours(data.avg_time_in_step_hours_30d)}
                </p>
                <p className="text-[10px] text-slate-500 font-medium mt-1">Avg / step (30d)</p>
              </div>
            </div>
          )}

          {!loading && !error && (
            <p className="text-[11px] text-slate-400 mt-3 text-center">
              {data.total_active} active workflow{data.total_active === 1 ? '' : 's'} in tenant • click to manage
            </p>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default WorkflowInsightsWidget;
